import { RequirementFriendlyDescription } from "../../requirementFriendlyDescription.js";
import { intoxicationTypeOptions, placeOfSafetyTypeOptions, s136ExtensionFormPart1DataSchema, } from "../../schemas/136/s136ExtensionForm.js";
import { calculateExtendedS136EndDate, calculateOriginalS136EndDate, } from "../../util/s136DateTime.js";
export const s136ExtensionFormPart1Template = {
    signing: {
        userOrTeamRequirement: {
            accreditation: ["doctor", "section-12"],
            teamType: ["guest"],
            friendlyDescription: RequirementFriendlyDescription.registeredMedicalPractitioner,
        },
    },
    dataSchema: s136ExtensionFormPart1DataSchema,
    formBuilderFields: [
        // Warning info text
        {
            type: "label",
            showStaticLabel: true,
            label: [
                "By completing this form, the Section 136 detention will be legally extended. Please inform any police officers involved.",
            ],
        },
        { type: "separator" },
        // Practitioner details
        {
            type: "practitioner",
            label: "My details",
            field: "user",
        },
        // Registered medical practitioner confirmation checkbox
        {
            type: "confirmation-checkbox",
            field: "isRegisteredMedicalPractitioner",
            checkboxLabel: "I am the registered medical practitioner responsible for the examination for this patient",
        },
        // GMC number — shown when the practitioner checkbox is ticked.
        // Required for guest users; optional for full users (who already
        // have verified accreditations on their profile).
        {
            type: "branch",
            field: "isRegisteredMedicalPractitioner",
            branches: [
                {
                    fieldValue: true,
                    hideLabel: true,
                    label: "Practitioner confirmed",
                    fieldsOwned: ["gmcNumber"],
                    fields: [
                        {
                            type: "textbox",
                            field: "gmcNumber",
                            label: "GMC number",
                        },
                    ],
                },
                {
                    fieldValue: false,
                    hideLabel: true,
                    label: "Practitioner not confirmed",
                    fieldsOwned: [],
                    fields: [],
                },
            ],
        },
        { type: "separator" },
        // Patient name (readonly)
        {
            type: "readonly",
            label: "Patient name",
            textFn: (data) => [data.patient?.name || ""],
            isReadOnlyPatientInfo: true,
        },
        { type: "separator" },
        // Place of Safety section
        {
            type: "label",
            labelType: "subtitle",
            label: ["Place of Safety"],
        },
        // Place of Safety picker
        {
            type: "place-of-safety-picker",
            field: "placeOfSafety",
            label: "Place of Safety",
        },
        // Place of Safety type dropdown
        {
            type: "dropdown",
            label: "Place of Safety type",
            valueField: "placeOfSafetyTypeValue",
            textField: "placeOfSafetyTypeText",
            options: placeOfSafetyTypeOptions.map((opt) => ({
                value: opt.value,
                label: opt.label,
            })),
        },
        { type: "separator" },
        // Why extension is needed - multi-select
        {
            type: "multi-select",
            label: "Why is it not practicable to complete a mental health assessment within 24 hours?",
            options: [
                { field: "reasonMentalHealth", label: "The person's mental health" },
                {
                    field: "reasonPhysicalHealth",
                    label: "The person's physical health",
                },
                { field: "reasonIntoxication", label: "Effects of intoxication" },
            ],
        },
        // Conditional intoxication type
        {
            type: "branch",
            field: "reasonIntoxication",
            branches: [
                {
                    fieldValue: true,
                    hideLabel: true,
                    label: "Intoxication selected",
                    fieldsOwned: ["intoxicationTypeValue", "intoxicationTypeText"],
                    fields: [
                        {
                            type: "dropdown",
                            label: "For audit purposes, intoxication is likely the effect of:",
                            valueField: "intoxicationTypeValue",
                            textField: "intoxicationTypeText",
                            options: intoxicationTypeOptions.map((opt) => ({
                                value: opt.value,
                                label: opt.label,
                            })),
                        },
                    ],
                },
                {
                    fieldValue: false,
                    hideLabel: true,
                    label: "Intoxication not selected",
                    fieldsOwned: [],
                    fields: [],
                },
            ],
        },
        // Info tooltip about delay
        {
            type: "label",
            showStaticLabel: true,
            indent: 1,
            label: [
                "Note: A delay in attendance by an Approved Mental Health Professional or medical practitioner is not a valid reason for extending detention.",
            ],
        },
        // Full reason text (optional)
        {
            type: "textbox",
            field: "fullReasonText",
            label: "Please give the full reason(s) why the patient's physical or mental condition prevents the assessment being completed within 24 hours hence requiring an extension (optional)",
            rows: "5",
        },
        { type: "separator" },
        // Detention timing section
        {
            type: "label",
            labelType: "subtitle",
            label: ["Detention Timing"],
        },
        // Start date and time
        {
            type: "date-time",
            field: "detentionStartDateTime",
            label: "Start date and time of detention",
            maximum: (today) => today,
        },
        // Original S136 end date (readonly - calculated as start + 24h)
        {
            type: "readonly",
            label: "Original Section 136 end date",
            textFn: (data) => {
                if (!data.detentionStartDateTime) {
                    return ["Please enter start date above"];
                }
                return [calculateOriginalS136EndDate(data.detentionStartDateTime)];
            },
        },
        // Extension hours dropdown
        {
            type: "dropdown",
            label: "By how many hours has the detention been extended?",
            valueField: "extensionHoursValue",
            textField: "extensionHoursText",
            options: [
                { value: "1", label: "1 hour" },
                { value: "2", label: "2 hours" },
                { value: "3", label: "3 hours" },
                { value: "4", label: "4 hours" },
                { value: "5", label: "5 hours" },
                { value: "6", label: "6 hours" },
                { value: "7", label: "7 hours" },
                { value: "8", label: "8 hours" },
                { value: "9", label: "9 hours" },
                { value: "10", label: "10 hours" },
                { value: "11", label: "11 hours" },
                { value: "12", label: "12 hours" },
            ],
        },
        // Police officer approval
        {
            type: "textbox",
            field: "policeOfficerName",
            label: "Please provide the name of the Police Officer of rank superintendent or higher who approved this extension",
        },
        // New S136 end date (readonly - calculated)
        {
            type: "readonly",
            label: "New Section 136 end date",
            textFn: (data) => {
                if (!data.detentionStartDateTime || !data.extensionHoursValue) {
                    return ["Please enter start date and extension hours above"];
                }
                return [
                    calculateExtendedS136EndDate(data.detentionStartDateTime, Number(data.extensionHoursValue)),
                ];
            },
        },
        { type: "separator" },
        // Warning banner
        {
            type: "label",
            bannerType: "warning",
            label: [
                "If responsibility of care is transferred between agencies during the period of detention, a copy of this form must be handed over with the patient.",
            ],
        },
    ],
};
export const s136ExtensionFormV1Base = {
    id: "s136-extension",
    version: "1.0",
    status: "active",
    formName: "S136 Extension",
    description: "Extension of a Section 136 detention",
    category: "136",
    section: "136",
    parts: [s136ExtensionFormPart1Template],
    extractGenericData: (data) => ({
        standalone: {
            effectiveDateTime: data[0].data.detentionStartDateTime,
        },
    }),
};
//# sourceMappingURL=s136ExtensionForm.js.map